// A product's paths, logged in (BUILD-PLAN stage 4). The product opens only for someone who has it:
// anyone else gets the same "nothing here" as an address that doesn't exist. A secured place shows
// the place secured page, and suspended or paused access the paused page, on every path of the
// product, because nothing in it opens (docs/data-model.md 3.2). Until a product moves in, its path
// sends people to where it lives now (config/products.js). Profit On Your Terms and the Accelerator
// open like every other product since 6 Oct (Adam, 5 Oct: build everything first; their paths still
// go to course.adamslee.co.uk and members.adamslee.co.uk wherever they haven't moved in). The 1-2-1
// also opens for an owner or admin at 2-step login,
// for Adam's view of a client's space (ClientSpace.dc.html's "Adam, as admin" Tweak). A product whose
// access has ended keeps its Resources page, with only its yours-for-life items, while it has any for
// them (hub.my_for_life; Adam, 3 Oct): every path of the product goes there, an archived product's
// included, and nothing else of it opens.
//
// An open product reads its own rows once (ProductData.jsx), writes when it was last opened (the
// Library's "used last first"; never while staff view as a member), and draws its frame: the header,
// the menu with the page they're on marked, then the page, with the payment banner on top while a
// payment is overdue and the welcome checklist on top of the home. A mastermind's header carries
// the member's group ("Group 2", ProductFrame.jsx frameChip). The page is the product's own screen
// where it has one (screens.js), else the shared page for it: the recording page for a week,
// Resources, Sessions, Recordings and the certificate. An online tool's page is always the shared
// tool runner (ToolPage.jsx).
import { useEffect, useState } from 'react'
import { useMember } from '../app/member.jsx'
import { useNow } from '../lib/clock.js'
import { navigate, useLocation } from '../lib/router.jsx'
import { useViewingAs, ReadOnlyNote } from '../lib/viewAs.jsx'
import { basePath, rowRedirect } from '../config/products.js'
import { SITE } from '../config/site.js'
import { ownedProducts, forLifeProducts, kindLine } from '../library/model.js'
import { useLibraryModel } from '../library/useModel.js'
import { NotFound } from '../shell/Shell.jsx'
import { ProductHolding, ProductPageHolding } from '../screens/Holding.jsx'
import SecuredPage from './SecuredPage.jsx'
import PausedPage from './PausedPage.jsx'
import { ProductFrame, frameChip } from './ProductFrame.jsx'
import { ProductDataProvider, useProductData } from './ProductData.jsx'
import { ProductBanner, productBanner } from './ProductBanner.jsx'
import { WelcomeChecklist } from './WelcomeChecklist.jsx'
import { RecordingPage } from './RecordingPage.jsx'
import { ResourcesPage, ForLifeResources } from './Resources.jsx'
import { SessionsPage } from './SessionsPage.jsx'
import { RecordingsPage } from './RecordingsPage.jsx'
import { CertificatePage } from './CertificatePage.jsx'
import { ToolPage, toolCard } from './ToolPage.jsx'
import { WeekChips } from './WeekChips.jsx'
import { activeFor, menuFor, routeFor } from './model.js'
import { PHASE_TWO_SLUGS, knownScreen, loadScreen, pageNames, prefixFor } from './screens.js'
import { markOpened } from './data.js'
import { useMastermind } from '../products/accelerator/useMastermind.js'

// Whether a mastermind's member has a goal, for its welcome checklist: null until its rows have come (so the checklist
// waits, rather than first drawing the goal item not done), false if they didn't; any other type's, false
function goalKnown(product, mm) {
  if (product?.type !== 'mastermind') return false
  if (mm.status === 'ready') return Boolean(mm.data?.goal)
  return mm.status === 'error' ? false : null
}

// Staff who may open a client's space as Adam: an owner or admin at 2-step login (whoami's
// staff_role is filled only then), not while viewing as a member
export function staffOpensSpace(product, who) {
  return product?.type === 'client_space' && !who?.viewing_as && ['owner', 'admin'].includes(who?.staff_role)
}

export default function ProductRoute({ path }) {
  const { lib, ensureLibrary, me } = useMember()
  const nowMs = useNow()
  useEffect(() => { ensureLibrary() }, [ensureLibrary])
  const base = basePath(path)
  const row = lib.data?.products.find(p => p.path === base) || null
  const redirect = rowRedirect(path, row)
  useEffect(() => { if (redirect) navigate(redirect, { replace: true }) }, [redirect])

  if (!lib.data) return lib.status === 'error' ? <NotFound /> : <div className="boot" />
  if (redirect) return <div className="boot" />
  if (!row) return <NotFound />
  const free = row.access_kind === 'free'
  const ownedAll = ownedProducts(lib.data, nowMs)
  const owned = ownedAll.get(row.id) || null
  const forLife = !free && !owned ? forLifeProducts(lib.data, ownedAll).get(row.id) || null : null
  if (forLife) {
    return (
      <ProductDataProvider key={row.id} productId={row.id} personId={me.person.id} type={row.type} slug={row.slug}>
        <ForLifePage path={path} product={row} />
      </ProductDataProvider>
    )
  }
  if (row.state !== 'live') return <NotFound />
  const staffView = !owned && staffOpensSpace(row, me.who)
  if (!free && !owned && !staffView) return <NotFound />
  if (owned && !free && owned.access.status === 'secured') return <SecuredPage key={row.id} product={row} owned={owned} />
  if (owned && !free && (owned.access.status === 'suspended' || owned.access.status === 'paused')) {
    return <PausedPage key={row.id} product={row} owned={owned} />
  }
  if (free || PHASE_TWO_SLUGS.includes(row.slug)) return <ProductHolding product={row} kind={kindLine(row)} />
  return (
    <ProductDataProvider key={row.id} productId={row.id} personId={me.person.id} type={row.type} slug={row.slug}>
      <ProductPage path={path} product={row} owned={owned} staffView={staffView} />
    </ProductDataProvider>
  )
}

// A product whose access has ended, while yours-for-life items remain for them: its Resources page
// with only those items, in its own look, and a menu of that 1 page, and the page of a tool among
// them (ToolPage.jsx). Any other path of the product comes to Resources. Nothing is written (it isn't
// theirs to use any more, so the Library's order is left be).
function ForLifePage({ path, product }) {
  const pd = useProductData()
  const model = useLibraryModel()
  const viewingAs = useViewingAs()
  const nowMs = useNow()
  const here = `${product.path || ''}/resources`
  const data = pd.data
  const route = routeFor(path, product, {})
  const lifeData = data ? { ...data, placements: (data.placements || []).filter(pl => pl.yours_for_life) } : null
  const tool = route.page === 'tool' && lifeData ? toolCard(lifeData, route.placementId, nowMs) : null
  // a tool's path waits for the rows to say whether it's 1 of theirs for life
  const stay = path === here || (route.page === 'tool' && (!data || Boolean(tool)))
  useEffect(() => { if (!stay) navigate(here, { replace: true }) }, [stay, here])
  const p = data?.product ? { ...product, ...data.product } : product
  const menu = [{ key: 'resources', label: 'Resources', href: here }]
  const banner = model?.banner ? { text: model.banner, target: model.bannerFor } : null
  // every path comes to Resources, so it's their home here; a tool's page is under it, and on a phone
  // its arrow goes to the product's own path, which comes back to Resources (ProductFrame.jsx phoneBack)
  const subPage = route.page === 'tool'

  if (!stay || pd.status === 'loading' || (route.page === 'tool' && !data)) {
    return <ProductFrame product={p} menu={false} subPage={subPage}><main className="pp-area" aria-busy="true" /></ProductFrame>
  }
  if (pd.status === 'error' || !data) {
    return (
      <ProductFrame product={p} menu={false} subPage={subPage}>
        <main className="pp-area">
          <section className="pp-failed">
            <h1 className="pp-failed-h1">That didn't load</h1>
            <p className="pp-failed-text">Check your connection and try again. If it keeps happening, email {SITE.helpEmail}.</p>
            <button type="button" className="pp-btn" onClick={pd.reload}>Try again</button>
          </section>
        </main>
      </ProductFrame>
    )
  }
  if (tool) {
    return (
      <ProductFrame product={p} menu={menu} active="resources" subPage title={`${p.name}, ${tool.title}`}>
        <main className="pp-area pp-area--tool">
          {viewingAs && <ReadOnlyNote className="pp-viewas" />}
          <ProductBanner banner={banner} />
          <ToolPage product={p} route={route} />
        </main>
      </ProductFrame>
    )
  }
  return (
    <ProductFrame product={p} menu={menu} active="resources" title={`${p.name}, Resources`}>
      <main className="pp-area pp-area--resources pp-area--for-life">
        {viewingAs && <ReadOnlyNote className="pp-viewas" />}
        <ProductBanner banner={banner} />
        <ForLifeResources product={p} />
      </main>
    </ProductFrame>
  )
}

// The Library's "used last first" (member_product_state.last_opened_at), once each time a product
// opens. Never while staff view as a member, nor for Adam opening a client's space.
function useMarkOpened(productId, staffView) {
  const { me } = useMember()
  const viewingAs = useViewingAs()
  const personId = me.person?.id
  useEffect(() => {
    if (viewingAs || staffView || !personId) return
    markOpened(personId, productId).catch(() => { /* the Library's order can wait */ })
  }, [productId, personId, viewingAs, staffView])
}

// The screens a page tries, in order: the product's own for the page, then the shared page, then
// the product's own for any page. The 1-2-1's pages are its own first. A tool's page is only ever
// the shared tool runner. A paid resource pack's home is its Resources (the product-types plan), as
// its menu is Home alone.
function candidates(prefix, page, type) {
  if (page === 'tool') return ['shared:tool']
  if (type === 'resource_pack' && page === 'home') return ['shared:resources']
  const own = prefix ? pageNames(page).map(n => prefix + n) : []
  const any = prefix ? [prefix] : []
  const shared = SHARED[page] ? [`shared:${page}`] : []
  if (type === 'client_space') return [...own, ...any, ...shared]
  return [...own, ...shared, ...any]
}

const SHARED = {
  week: RecordingPage, resources: ResourcesPage, sessions: SessionsPage, recordings: RecordingsPage, certificate: CertificatePage,
  tool: ToolPage,
}

// The first screen that exists, or null; undefined while the bundle answers (at once)
function useScreen(names) {
  const key = names.join('|')
  const sync = () => {
    for (const n of names) {
      if (n.startsWith('shared:')) return SHARED[n.slice(7)]
      const s = knownScreen(n)
      if (s === undefined) return undefined
      if (s) return s
    }
    return null
  }
  const [, bump] = useState(0)
  const found = sync()
  useEffect(() => {
    if (found !== undefined) return undefined
    let live = true
    Promise.all(names.filter(n => !n.startsWith('shared:')).map(loadScreen)).then(() => { if (live) bump(x => x + 1) })
    return () => { live = false }
  }, [key, found])
  return found
}

function ProductPage({ path, product, owned, staffView }) {
  const pd = useProductData()
  const model = useLibraryModel()
  const viewingAs = useViewingAs()
  const { query } = useLocation()
  const { me } = useMember()
  useMarkOpened(product.id, staffView)

  const data = pd.data
  const p = data?.product ? { ...product, ...data.product } : product
  const route = data ? routeFor(path, p, data) : { page: 'home', segments: [] }
  // a mastermind's welcome checklist, on its home, knows whether they have a goal (its own rows, which its home reads
  // anyway: the Accelerator critique's member 12, 20 Oct); no other page or type reads anything more
  const mm = useMastermind(product.id, me.person.id, { enabled: product.type === 'mastermind' && !staffView && Boolean(data) && route.page === 'home' })
  const menu = data ? menuFor(p, data) : false
  const prefix = prefixFor(p)
  const Screen = useScreen(data ? candidates(prefix, route.page, p.type) : [])
  const nowMs = useNow()
  const chip = frameChip(p, owned)
  // any path under the product's own is a sub-page, from the first draw, so a link straight to one
  // has its phone arrow to the product's home before the rows come (ProductFrame.jsx phoneBack)
  const subPage = path.replace(/\/+$/, '') !== (p.path || '')

  if (pd.status === 'loading' || (data && Screen === undefined)) {
    return <ProductFrame product={p} chip={chip} menu={false} subPage={subPage}><main className="pp-area" aria-busy="true" /></ProductFrame>
  }
  if (pd.status === 'error' || !data) {
    return (
      <ProductFrame product={p} chip={chip} menu={false} subPage={subPage}>
        <main className="pp-area">
          <section className="pp-failed">
            <h1 className="pp-failed-h1">That didn't load</h1>
            <p className="pp-failed-text">Check your connection and try again. If it keeps happening, email {SITE.helpEmail}.</p>
            <button type="button" className="pp-btn" onClick={pd.reload}>Try again</button>
          </section>
        </main>
      </ProductFrame>
    )
  }
  if (route.page === 'other' && !Screen) return <NotFound />
  if (route.page === 'certificate' && !p.certificate_on && !data.certificate) return <NotFound />
  // a tool's page only for a tool placed in this product for them
  const tool = route.page === 'tool' ? toolCard(data, route.placementId, nowMs) : null
  if (route.page === 'tool' && !tool) return <NotFound />

  const active = activeFor(route.page, menu, route)
  const titleBits = {
    home: null, week: route.module?.label || route.recording?.title, certificate: 'Certificate', 'accountability-log': 'Accountability log', tool: tool?.title,
    'start-here': menu.find(m => m.key === 'start-here')?.label || 'Start here',
  }
  const sub = route.page in titleBits ? titleBits[route.page] : menu.find(m => m.key === route.page)?.label
  // the product's own name, the masterclass's included, as the Library's tile and search have it
  const title = sub ? `${p.name}, ${sub}` : p.name
  const banner = owned ? productBanner(model, owned) : model?.banner ? { text: model.banner, target: model.bannerFor } : null
  const fullRoute = { ...route, path, query }

  // a new page starts fresh: a week's player, its minute and its questions don't carry to the next
  const key = `${route.page}:${route.recording?.id || route.segments.join('/')}`
  let content
  if (Screen) content = <Screen key={key} product={p} owned={owned} staffView={staffView} route={fullRoute} />
  else if (route.page === 'home') content = <ProductPageHolding product={p} />
  else content = <ProductPageHolding product={p} back />

  return (
    <ProductFrame product={p} chip={chip} menu={menu} active={active} title={title} subPage={subPage}
      phoneNav={route.page === 'week' && route.module ? <WeekChips product={p} data={data} current={route.module} /> : null}>
      <main className={`pp-area pp-area--${route.page}`}>
        {viewingAs && <ReadOnlyNote className="pp-viewas" />}
        <ProductBanner banner={banner} />
        {route.page === 'home' && !staffView && <WelcomeChecklist product={p} goalExists={goalKnown(p, mm)} />}
        {content}
      </main>
    </ProductFrame>
  )
}
